import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authAPI } from '../api/auth';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const REDIRECT_DELAY_MS = 3000;

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const token = searchParams.get('token') || '';

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        return;
      }
      try {
        await authAPI.verifyEmail(token);
        setStatus('success');
      } catch {
        setStatus('error');
      }
    };
    verify();
  }, [token]);

  // Once verified, take the customer straight to shopping rather than
  // leaving them stranded on a confirmation screen — with a short delay
  // so the success message still registers, and a manual button as a
  // fallback in case they want to go immediately.
  useEffect(() => {
    if (status !== 'success') return;
    const timer = setTimeout(() => navigate('/shop'), REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [status, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-white">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-sm text-center"
      >
        <Link to="/" className="block mb-12">
          <img src="/logo.png" alt="Lanre Savage" className="h-16 mx-auto" />
        </Link>

        {status === 'loading' && (
          <div className="space-y-6">
            <LoadingSpinner size="lg" />
            <p className="text-xs tracking-widest uppercase text-nude">
              Verifying your email...
            </p>
          </div>
        )}

        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-6"
          >
            <div className="w-16 h-16 bg-light rounded-full flex items-center justify-center mx-auto">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                strokeLinejoin="round" className="text-primary">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h1 className="font-serif text-3xl text-primary">
              Email Verified
            </h1>
            <p className="text-xs text-nude tracking-wide leading-relaxed">
              Your email has been verified successfully. Taking you to the shop now...
            </p>
            <Link to="/shop" className="btn-primary block">
              Continue to Shop
            </Link>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-6"
          >
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                strokeLinejoin="round" className="text-red-500">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <h1 className="font-serif text-3xl text-primary">
              Verification Failed
            </h1>
            <p className="text-xs text-nude tracking-wide leading-relaxed">
              The verification link is invalid or has expired. Please request
              a new verification email.
            </p>
            <Link to="/account" className="btn-primary block">
              Go to Account
            </Link>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;